import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import PageHeader from '../../components/shared/PageHeader';
import { Card } from '../../components/shared/DataToolbar';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { getProperty, createProperty, updateProperty, stripeConnect } from '../../api/hostApi';
import { AlertCircle, ArrowLeft, CheckCircle2, Loader2, Save, Wallet } from 'lucide-react';

const emptyForm = {
  title: '', slug: '', subtitle: '', description: '',
  city: '', state: '', country: 'USA', currency: 'USD',
  price_per_night: 250, beds: 1, baths: 1, max_guests: 2,
  check_in_time: '15:00', check_out_time: '11:00', status: 'draft',
};

const US_STATES = ['California','New York','Texas','Florida','Washington','Colorado','Nevada','Illinois','Massachusetts','Georgia','Arizona','Oregon'];

const normalizeProperty = (property = {}) => ({
  ...emptyForm,
  ...property,
  beds: property.beds ?? property.bedrooms ?? emptyForm.beds,
  baths: property.baths ?? property.bathrooms ?? emptyForm.baths,
});

export default function PropertyForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [payoutLoading, setPayoutLoading] = useState(true);
  const [payoutData, setPayoutData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setPayoutLoading(true);
    stripeConnect()
      .then((data) => { if (!cancelled) setPayoutData(data); })
      .catch(() => { if (!cancelled) setPayoutData(null); })
      .finally(() => { if (!cancelled) setPayoutLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!isEdit) return undefined;
    setLoading(true);
    getProperty(id)
      .then((property) => { if (!cancelled) setForm(normalizeProperty(property)); })
      .catch((error) => toast.error(error?.response?.data?.message || error?.message || 'Failed to load property'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, isEdit]);

  const payoutActive = Boolean(payoutData?.active);
  const payoutAccount = payoutData?.account || null;
  const payoutStatus = payoutActive
    ? 'active'
    : (payoutAccount?.onboarding_status || (payoutAccount ? 'pending' : 'not_started'));

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const setPublicationStatus = (value) => {
    if (value === 'published' && !payoutActive) {
      toast.error('Complete Stripe payout setup before publishing a property.');
      return;
    }
    setField('status', value);
  };

  const submit = async (event) => {
    event.preventDefault();

    if (form.status === 'published' && !payoutActive) {
      toast.error('Complete Stripe payout setup before publishing a property.');
      return;
    }

    setSaving(true);
    const payload = {
      title: form.title,
      slug: form.slug || null,
      subtitle: form.subtitle || null,
      description: form.description || null,
      city: form.city || null,
      state: form.state || null,
      country: form.country || 'USA',
      currency: form.currency || 'USD',
      price_per_night: Number(form.price_per_night || 0),
      beds: Number(form.beds || 0),
      baths: Number(form.baths || 0),
      max_guests: Number(form.max_guests || 1),
      check_in_time: form.check_in_time || null,
      check_out_time: form.check_out_time || null,
      status: form.status || 'draft',
    };

    try {
      if (isEdit) {
        await updateProperty(id, payload);
        toast.success('Property updated');
        nav(`/host/properties/${id}`);
      } else {
        const created = await createProperty(payload);
        toast.success('Property created');
        nav(`/host/properties/${created.id}`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl">
      <PageHeader breadcrumbs={['Properties', isEdit ? 'Edit' : 'New']} title={isEdit ? 'Edit property' : 'New property'} description="Basic listing details. Images, features, rules, policies, FAQs and availability are managed from the property page." actions={<Button asChild variant="outline" className="h-10"><Link to="/host/properties"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Link></Button>} />
      {loading && <Card className="p-16 grid place-items-center"><Loader2 className="h-6 w-6 text-teal-600 animate-spin" /></Card>}
      {!loading && (
        <form onSubmit={submit} className="space-y-6">
          <Card className="p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Basic information</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setField('title', e.target.value)} className="mt-1.5" required /></div>
              <div><Label>Slug (optional)</Label><Input value={form.slug || ''} onChange={(e) => setField('slug', e.target.value)} className="mt-1.5" /></div>
              <div className="md:col-span-2"><Label>Subtitle</Label><Input value={form.subtitle || ''} onChange={(e) => setField('subtitle', e.target.value)} className="mt-1.5" /></div>
              <div className="md:col-span-2"><Label>Description</Label><Textarea value={form.description || ''} onChange={(e) => setField('description', e.target.value)} className="mt-1.5" rows={6} /></div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Location</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div><Label>City</Label><Input value={form.city || ''} onChange={(e) => setField('city', e.target.value)} className="mt-1.5" /></div>
              <div><Label>State</Label><Select value={form.state || ''} onValueChange={(v) => setField('state', v)}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select state" /></SelectTrigger><SelectContent>{US_STATES.map((state) => <SelectItem key={state} value={state}>{state}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Country</Label><Input value={form.country || 'USA'} onChange={(e) => setField('country', e.target.value)} className="mt-1.5" /></div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Capacity & pricing</h3>
            <div className="grid md:grid-cols-4 gap-4">
              <div><Label>Price / night</Label><div className="relative mt-1.5"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span><Input type="number" min="0" step="0.01" value={form.price_per_night} onChange={(e) => setField('price_per_night', e.target.value)} className="pl-7" /></div></div>
              <div><Label>Currency</Label><Input value={form.currency || 'USD'} onChange={(e) => setField('currency', e.target.value.toUpperCase())} maxLength={3} className="mt-1.5" /></div>
              <div><Label>Beds</Label><Input type="number" min="0" value={form.beds} onChange={(e) => setField('beds', e.target.value)} className="mt-1.5" /></div>
              <div><Label>Baths</Label><Input type="number" min="0" value={form.baths} onChange={(e) => setField('baths', e.target.value)} className="mt-1.5" /></div>
              <div><Label>Max guests</Label><Input type="number" min="1" value={form.max_guests} onChange={(e) => setField('max_guests', e.target.value)} className="mt-1.5" /></div>
              <div><Label>Check-in</Label><Input type="time" value={form.check_in_time || ''} onChange={(e) => setField('check_in_time', e.target.value)} className="mt-1.5" /></div>
              <div><Label>Check-out</Label><Input type="time" value={form.check_out_time || ''} onChange={(e) => setField('check_out_time', e.target.value)} className="mt-1.5" /></div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
              <div className="max-w-md">
                <h3 className="font-semibold text-slate-900">Publication</h3>
                <p className="mt-1 text-sm text-slate-500">A property can be saved as a draft at any time. Stripe payout setup must be active before it can be published.</p>
                <div className="mt-4 max-w-sm">
                  <Label>Status</Label>
                  <Select value={form.status || 'draft'} onValueChange={setPublicationStatus}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published" disabled={!payoutActive}>Published</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className={`w-full rounded-xl border p-4 md:max-w-sm ${payoutActive ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
                <div className="flex items-start gap-3">
                  {payoutLoading ? (
                    <Loader2 className="mt-0.5 h-5 w-5 animate-spin text-slate-500" />
                  ) : payoutActive ? (
                    <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
                  ) : (
                    <AlertCircle className="mt-0.5 h-5 w-5 text-amber-600" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900">Stripe payout setup</div>
                    <div className="mt-1 text-sm text-slate-600">
                      {payoutLoading
                        ? 'Checking payout status…'
                        : payoutActive
                          ? 'Active. This host can publish properties.'
                          : `Not ready (${String(payoutStatus).replaceAll('_', ' ')}). Publishing is disabled.`}
                    </div>
                    {!payoutLoading && !payoutActive && (
                      <Button asChild size="sm" className="mt-3 bg-teal-700 hover:bg-teal-800">
                        <Link to="/host/payout-setup"><Wallet className="mr-1.5 h-4 w-4" /> Set up payouts</Link>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <div className="flex items-center gap-3 justify-end"><Button type="button" variant="outline" onClick={() => nav('/host/properties')}>Cancel</Button><Button type="submit" disabled={saving} className="bg-teal-700 hover:bg-teal-800">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> {isEdit ? 'Save changes' : 'Create property'}</>}</Button></div>
        </form>
      )}
    </div>
  );
}
